import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { PowerProfileService } from '@services/power-profile.service';
import { PowerProfile, PowerProfileDTO } from '@app/shared/models/power-profile.interface';
import { SensorNodeService } from '@services/sensor-node.service';
import { SensorNode } from '@app/shared/models/sensor-node';
import { ToastService } from '@services/toast/toast.service';
import { DISCRIMINATOR, Selectable } from '@app/shared/models/selectable.interface';
import {
  PowerProfileDialogComponent,
  PowerProfileDialogData,
  PowerProfileDialogResult
} from './power-profile-dialog/power-profile-dialog.component';

@Component({
  selector: 'app-power-profile-form',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatFormFieldModule, MatSelectModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './power-profile-form.component.html',
  styleUrl: './power-profile-form.component.scss'
})
export class PowerProfileFormComponent implements OnInit {
  @Input() buildingId: number;
  @Input() floorId: number;

  private powerProfileService = inject(PowerProfileService);
  private nodeService = inject(SensorNodeService);
  private toastService = inject(ToastService);
  private dialog = inject(MatDialog);

  readonly availableProfiles = signal<PowerProfile[]>([]);
  readonly selectedProfileId = signal<number | null>(null);
  readonly areNodesSelected = signal(false);
  private selectedNodeIds: number[] = [];

  readonly canEdit = computed(() => {
    const id = this.selectedProfileId();
    if (id == null) return false;
    const profile = this.availableProfiles().find((p) => p.id === id);
    return profile != null && !!profile.buildingId;
  });

  readonly canAssign = computed(() => this.selectedProfileId() != null && this.areNodesSelected());

  ngOnInit(): void {
    this.nodeService.buildingProfiles$.subscribe((profiles) => {
      this.availableProfiles.set(profiles);
    });

    this.nodeService.getCurrentFloorSelectedEntities$().subscribe((entities: Selectable[]) => {
      const nodes = entities.filter(
        (e): e is SensorNode =>
          e.discriminator !== DISCRIMINATOR.LUMINAIRE && e.discriminator !== DISCRIMINATOR.EM_DRIVER
      );
      this.areNodesSelected.set(nodes.length > 0);
      this.selectedNodeIds = nodes.map((n) => n.id);

      const profileIds = new Set(nodes.map((n) => n.powerProfile?.id).filter((id) => id != null));
      this.selectedProfileId.set(profileIds.size === 1 ? [...profileIds][0] : null);
    });
  }

  openCreateDialog(): void {
    const data: PowerProfileDialogData = { buildingId: this.buildingId, mode: 'create' };
    this.dialog
      .open<PowerProfileDialogComponent, PowerProfileDialogData, PowerProfileDialogResult>(
        PowerProfileDialogComponent,
        {
          width: '480px',
          data
        }
      )
      .afterClosed()
      .subscribe((result) => {
        if (!result) return;
        const dto: PowerProfileDTO = { ...result, buildingId: this.buildingId };
        this.powerProfileService.createProfile(dto, this.buildingId).subscribe({
          next: (created) => {
            this.toastService.success({ message: 'Power profile created.', dataCy: 'power-profile-form-success' });
            this.nodeService.fetchNodes(this.floorId, this.buildingId);
            this.selectedProfileId.set(created.id);
          },
          error: (err: HttpErrorResponse | any) =>
            this.toastService.error({
              message: this.getErrorMessage(err, 'Failed to create power profile.'),
              dataCy: 'power-profile-form-error'
            })
        });
      });
  }

  openEditDialog(): void {
    const id = this.selectedProfileId();
    const profile = this.availableProfiles().find((p) => p.id === id);
    if (!profile) return;

    const data: PowerProfileDialogData = { buildingId: this.buildingId, mode: 'edit', profile };
    this.dialog
      .open<PowerProfileDialogComponent, PowerProfileDialogData, PowerProfileDialogResult>(
        PowerProfileDialogComponent,
        {
          width: '480px',
          data
        }
      )
      .afterClosed()
      .subscribe((result) => {
        if (!result) return;
        const updated: PowerProfile = { ...profile, ...result };
        this.powerProfileService.updateProfile(updated, this.buildingId).subscribe({
          next: () => {
            this.toastService.success({ message: 'Power profile updated.', dataCy: 'power-profile-update-success' });
            this.nodeService.fetchNodes(this.floorId, this.buildingId);
          },
          error: (err: HttpErrorResponse | any) =>
            this.toastService.error({
              message: this.getErrorMessage(err, 'Failed to update power profile.'),
              dataCy: 'power-profile-update-error'
            })
        });
      });
  }

  assign(): void {
    const profileId = this.selectedProfileId();
    if (profileId == null || this.selectedNodeIds.length === 0) return;

    this.powerProfileService.assignNodes(profileId, this.selectedNodeIds).subscribe({
      next: () => {
        this.toastService.success({ message: 'Power profile assigned.', dataCy: 'power-profile-assign-success' });
        this.nodeService.fetchNodes(this.floorId, this.buildingId);
      },
      error: (err: HttpErrorResponse | any) =>
        this.toastService.error({
          message: this.getErrorMessage(err, 'Failed to assign power profile.'),
          dataCy: 'power-profile-assign-error'
        })
    });
  }

  private getErrorMessage(error: any, fallback: string): string {
    if (typeof error?.error === 'string' && error.error.trim().length > 0) {
      return error.error;
    }
    if (Array.isArray(error?.error) && error.error.length > 0) {
      return error.error
        .map((e: any) => (typeof e === 'string' ? e : e?.message || e?.defaultMessage || JSON.stringify(e)))
        .join(', ');
    }
    if (error?.error?.message && typeof error.error.message === 'string' && error.error.message.trim().length > 0) {
      return error.error.message;
    }
    if (error?.error?.error && typeof error.error.error === 'string' && error.error.error.trim().length > 0) {
      return error.error.error;
    }
    return fallback;
  }
}

